/**
 * Premium racetrack table — matches reference design:
 * dark emerald felt, double gold oval, leather rail with gold segments, top dealer tray.
 */
import type { SeatLayoutProfile } from 'constants/seatLayout';
import { DISTRIBUTE_POSITION, getPhysicalSeats, PORTRAIT_DISTRIBUTE_POSITION } from 'constants/seatLayout';

export const TABLE_VIEWBOX = { w: 520, h: 260 };
/** Portrait racetrack — same stadium as landscape, stood on end (2:3) */
export const TABLE_VIEWBOX_PORTRAIT = { w: 360, h: 540 };

const OUTER = { x: 2, y: 2, w: 516, h: 256, rx: 128, ry: 128 };
const WOOD = { x: 8, y: 8, w: 504, h: 244, rx: 122, ry: 122 };
const RAIL_OUT = { x: 14, y: 14, w: 492, h: 232, rx: 116, ry: 116 };
const RAIL_IN = { x: 22, y: 22, w: 476, h: 216, rx: 108, ry: 108 };
const FELT = { x: 32, y: 26, w: 456, h: 208, rx: 104, ry: 104 };

/** Portrait — Octro-style vertical pill (straight sides, round caps). rx = ry = half width. */
const OUTER_P = { x: 6, y: 6, w: 348, h: 528, rx: 174, ry: 174 };
const WOOD_P = { x: 12, y: 12, w: 336, h: 516, rx: 168, ry: 168 };
const RAIL_OUT_P = { x: 20, y: 20, w: 320, h: 500, rx: 160, ry: 160 };
const RAIL_IN_P = { x: 42, y: 42, w: 276, h: 456, rx: 138, ry: 138 };
const FELT_P = { x: 52, y: 52, w: 256, h: 436, rx: 128, ry: 128 };

interface Props {
  showSeatGuides?: boolean;
  previewMode?: boolean;
  liveMode?: boolean;
  layoutProfile?: SeatLayoutProfile;
  className?: string;
}

function pct(x: number, y: number, viewW: number, viewH: number) {
  return { x: (x / 100) * viewW, y: (y / 100) * viewH };
}

function TableDefs() {
  return (
    <defs>
      <linearGradient id="tpWood" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4a3020" />
        <stop offset="50%" stopColor="#3d2618" />
        <stop offset="100%" stopColor="#1a0e08" />
      </linearGradient>
      <linearGradient id="tpLeather" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#8B5E3C" />
        <stop offset="30%" stopColor="#6B4423" />
        <stop offset="100%" stopColor="#3d2818" />
      </linearGradient>
      <linearGradient id="tpLeatherHi" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
        <stop offset="100%" stopColor="rgba(0,0,0,0.2)" />
      </linearGradient>
      <linearGradient id="tpGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFF8DC" />
        <stop offset="35%" stopColor="#F5D76E" />
        <stop offset="65%" stopColor="#D4AF37" />
        <stop offset="100%" stopColor="#8B6914" />
      </linearGradient>
      <radialGradient id="tpFelt" cx="50%" cy="42%" r="75%">
        <stop offset="0%" stopColor="#0a5a42" />
        <stop offset="35%" stopColor="#073d2e" />
        <stop offset="70%" stopColor="#052a20" />
        <stop offset="100%" stopColor="#031812" />
      </radialGradient>
      <pattern id="tpFeltCross" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="12" height="12" fill="transparent" />
        <line x1="0" y1="0" x2="0" y2="12" stroke="rgba(255,255,255,0.025)" strokeWidth="0.6" />
        <line x1="6" y1="0" x2="6" y2="12" stroke="rgba(0,0,0,0.12)" strokeWidth="0.5" />
      </pattern>
      <pattern id="tpFeltDiamond" width="16" height="16" patternUnits="userSpaceOnUse">
        <path d="M8 0 L16 8 L8 16 L0 8 Z" fill="none" stroke="rgba(255,255,255,0.018)" strokeWidth="0.5" />
      </pattern>
      <radialGradient id="tpFeltSpot" cx="50%" cy="38%" r="40%">
        <stop offset="0%" stopColor="rgba(255,255,255,0.12)" />
        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
      </radialGradient>
      <filter id="tpDrop" x="-10%" y="-14%" width="120%" height="128%">
        <feDropShadow dx="0" dy="16" stdDeviation="18" floodColor="#000" floodOpacity="0.8" />
      </filter>
      <filter id="tpDropPortrait" x="-20%" y="-8%" width="140%" height="120%">
        <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#000" floodOpacity="0.65" />
      </filter>
      <filter id="tpTrayShadow">
        <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.55" />
      </filter>
      <filter id="tpFeltTex" x="0%" y="0%" width="100%" height="100%">
        <feTurbulence type="fractalNoise" baseFrequency="0.75 0.75" numOctaves="3" seed="8" result="n" />
        <feColorMatrix in="n" type="saturate" values="0" result="gn" />
        <feBlend in="SourceGraphic" in2="gn" mode="multiply" />
      </filter>
    </defs>
  );
}

function DealerShufflerTray({
  distribute,
  viewW,
  viewH,
}: {
  distribute: { x: number; y: number };
  viewW: number;
  viewH: number;
}) {
  const d = pct(distribute.x, distribute.y, viewW, viewH);
  const tx = d.x - 38;
  const ty = d.y - 12;
  return (
    <g aria-label="Dealer shuffler" filter="url(#tpTrayShadow)">
      <rect x={tx - 1} y={ty + 2} width={78} height={26} rx={4} fill="rgba(0,0,0,0.5)" />
      <rect x={tx} y={ty} width={76} height={24} rx={3} fill="url(#tpGold)" opacity={0.95} />
      <rect x={tx + 2} y={ty + 2} width={72} height={20} rx={2} fill="#0a0806" />
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <rect
          key={i}
          x={tx + 6 + i * 8}
          y={ty + 4}
          width={5}
          height={16}
          rx={0.5}
          fill="#1a1510"
          stroke="rgba(212,175,55,0.15)"
          strokeWidth={0.3}
        />
      ))}
    </g>
  );
}

function SeatRing({ x, y }: { x: number; y: number }) {
  return (
    <g opacity={0.35}>
      <ellipse cx={x} cy={y + 1} rx={14} ry={7} fill="rgba(0,0,0,0.45)" />
      <ellipse cx={x} cy={y} rx={12} ry={6} fill="none" stroke="rgba(212,175,55,0.2)" strokeWidth={0.8} />
    </g>
  );
}

function TableBody({
  outer,
  wood,
  railOut,
  railIn,
  felt,
  shadowCx,
  shadowCy,
  shadowRx,
  shadowRy,
  feltSpotCx,
  feltSpotCy,
  feltSpotRx,
  feltSpotRy,
  isPortrait = false,
}: {
  outer: typeof OUTER;
  wood: typeof WOOD;
  railOut: typeof RAIL_OUT;
  railIn: typeof RAIL_IN;
  felt: typeof FELT;
  shadowCx: number;
  shadowCy: number;
  shadowRx: number;
  shadowRy: number;
  feltSpotCx: number;
  feltSpotCy: number;
  feltSpotRx: number;
  feltSpotRy: number;
  isPortrait?: boolean;
}) {
  return (
    <>
      <ellipse cx={shadowCx} cy={shadowCy} rx={shadowRx} ry={shadowRy} fill="rgba(0,0,0,0.6)" />
      <rect {...outer} fill="#060404" filter={isPortrait ? 'url(#tpDropPortrait)' : 'url(#tpDrop)'} />
      <rect {...wood} fill="url(#tpWood)" />
      <rect {...railOut} fill="url(#tpLeather)" />
      <rect {...railOut} fill="url(#tpLeatherHi)" opacity={0.4} />
      <rect
        x={railOut.x + 3}
        y={railOut.y + 3}
        width={railOut.w - 6}
        height={railOut.h - 6}
        rx={railOut.rx - 3}
        ry={railOut.ry - 3}
        fill="none"
        stroke="url(#tpGold)"
        strokeWidth={isPortrait ? 3.2 : 1.8}
        opacity={0.95}
      />
      <rect {...railIn} fill="none" stroke="rgba(0,0,0,0.5)" strokeWidth={3} opacity={0.4} />
      <g filter="url(#tpFeltTex)">
        <rect {...felt} fill="url(#tpFelt)" />
        <rect {...felt} fill="url(#tpFeltCross)" opacity={0.85} />
        <rect {...felt} fill="url(#tpFeltDiamond)" opacity={0.9} />
      </g>
      <rect {...felt} fill="url(#tpFeltSpot)" />
      <rect
        x={felt.x + 18}
        y={felt.y + 14}
        width={felt.w - 36}
        height={felt.h - 28}
        rx={felt.rx - 14}
        ry={felt.ry - 14}
        fill="none"
        stroke="rgba(212,175,55,0.12)"
        strokeWidth={0.7}
      />
      <ellipse cx={feltSpotCx} cy={feltSpotCy} rx={feltSpotRx} ry={feltSpotRy} fill="rgba(0,0,0,0.08)" />
    </>
  );
}

export default function TeenPattiTableTopDown({
  showSeatGuides = false,
  previewMode = false,
  liveMode = true,
  layoutProfile = 'desktop',
  className = '',
}: Props) {
  const isPortrait = layoutProfile === 'portrait';
  const viewW = isPortrait ? TABLE_VIEWBOX_PORTRAIT.w : TABLE_VIEWBOX.w;
  const viewH = isPortrait ? TABLE_VIEWBOX_PORTRAIT.h : TABLE_VIEWBOX.h;
  const physicalSeats = getPhysicalSeats(layoutProfile);
  const distribute = isPortrait ? PORTRAIT_DISTRIBUTE_POSITION : DISTRIBUTE_POSITION;

  const outer = isPortrait ? OUTER_P : OUTER;
  const wood = isPortrait ? WOOD_P : WOOD;
  const railOut = isPortrait ? RAIL_OUT_P : RAIL_OUT;
  const railIn = isPortrait ? RAIL_IN_P : RAIL_IN;
  const felt = isPortrait ? FELT_P : FELT;

  const feltSpotCx = felt.x + felt.w / 2;
  const feltSpotCy = felt.y + felt.h / 2 + 2;
  const feltSpotRx = isPortrait ? 48 : 72;
  const feltSpotRy = isPortrait ? 110 : 44;

  const shadowCx = viewW / 2;
  const shadowCy = isPortrait ? viewH - 6 : viewH - 2;
  const shadowRx = isPortrait ? 120 : 248;
  const shadowRy = isPortrait ? 16 : 20;

  return (
    <svg
      className={`tp-table-asset block h-full w-full ${isPortrait ? 'tp-table-asset-portrait' : ''} ${className}`}
      viewBox={`0 0 ${viewW} ${viewH}`}
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Teen Patti table"
    >
      <TableDefs />
      <TableBody
        outer={outer}
        wood={wood}
        railOut={railOut}
        railIn={railIn}
        felt={felt}
        shadowCx={shadowCx}
        shadowCy={shadowCy}
        shadowRx={shadowRx}
        shadowRy={shadowRy}
        feltSpotCx={feltSpotCx}
        feltSpotCy={feltSpotCy}
        feltSpotRx={feltSpotRx}
        feltSpotRy={feltSpotRy}
        isPortrait={isPortrait}
      />
      {!liveMode && (
        <DealerShufflerTray distribute={distribute} viewW={viewW} viewH={viewH} />
      )}
      {physicalSeats.map((s) => {
        const p = pct(s.x, s.y, viewW, viewH);
        return <SeatRing key={s.id} x={p.x} y={p.y} />;
      })}
      {showSeatGuides &&
        physicalSeats.map((s) => {
          const p = pct(s.x, s.y, viewW, viewH);
          return (
            <text
              key={`lbl-${s.id}`}
              x={p.x}
              y={p.y + (s.hero ? 18 : s.y > 50 ? 14 : -6)}
              textAnchor="middle"
              fontSize={7}
              fontWeight={600}
              fill="#D4AF37"
              opacity={previewMode ? 1 : 0.7}
              fontFamily="system-ui,sans-serif"
            >
              {s.hero ? 'YOU' : `S${s.seatNumber}`}
            </text>
          );
        })}
    </svg>
  );
}
